<template>
  <nav class="products text-white hidden md:flex md:h-screen md:fixed">
    <header>
      <transition name="header-fade">
        <a
          v-if="mainMenuOpen"
          :href="tithelyHome">
          <InlineSVG :src="require(`./assets/logo.svg`)"/>
        </a>
        <div
          v-else
          class="toggle-main-menu"
        >
          <button type="button" @click="toggleMainMenu(! mainMenuOpen)" class="text-white">
            <InlineSVG :src="require(`./assets/toggle.svg`)"/>
          </button>
        </div>
      </transition>
    </header>

    <section>
      <div
          v-for="{ name, route, url, icon, key } in apps"
          :class="[`product`, `${key}`]">
          <a
            v-if="route"
            :href="resolveRoute(route)"
            :class="{'link-active': key == 'media'}"
          >
            <InlineSVG :src="icon"/>
          </a>
          <a
            v-else
            :href="url">
            <InlineSVG :src="icon"/>
          </a>
          <div class="label">{{ name }}</div>
      </div>
      <div
        v-if="!apps.length"
        class="product">
        <a :href="legacyURL">
          <InlineSVG :src="require(`./assets/legacy.svg`)"/>
        </a>
        <div class="label">Tithe.ly 1.0</div>
      </div>
    </section>

    <footer>
      <button
        type="button"
        class="help"
        data-pendo-help-menu>
        <InlineSVG :src="require(`./assets/support.svg`)"/>
      </button>

      <div
        class="popup-menu">
        <button
          type="button"
          class="user"
          @click.stop="userMenu(`toggle`)">
          <UserAvatar />
        </button>

        <ul v-show="userMenuOpen" v-on-clickaway="userMenu">
          <li class="user-details">
            <div class="name">
                {{ user.name }}
                <!-- <div class="organization">{{ organization.name }}</div> -->
            </div>
          </li>
          <li class="separator"/>
          <li>
            <a :href="ssoCallbackUrl">
              Product Suite
            </a>
          </li>
          <li>
            <a :href="`${ssoCallbackUrl}billing`">
              Billing
            </a>
          </li>
          <li>
            <a :href="$route('logout')">
              {{ 'Log Out' }}
            </a>
          </li>
        </ul>
      </div>
    </footer>
  </nav>
</template>

<script>
import Vue from 'vue'
import { mixin as clickaway } from 'vue-clickaway'
import { filter } from 'lodash'
import InlineSVG from 'vue-inline-svg'
import UserAvatar from './UserAvatar.vue'
import apps from './apps'

export default {
  name: `AppsMenu`,

  props: {
    user: Object,
    open: Boolean,
  },

  mixins: [ clickaway ],

  components: {
    InlineSVG,
    UserAvatar,
  },

  data() {
    return {
      mainMenuOpen: this.open,
      userMenuOpen: false,
      legacyURL: `${process.env.MIX_GIVING_HOST_V1}/user`,
      tithelyHome: process.env.MIX_TITHELY_HOME,
    }
  },

  computed: {
    apps() {
      if (this.appAccess === null || this.appAccess.length === 0) {
        return []
      }

      return apps.filter(app => this.appAccess.includes(app.key))
    },

    appAccess() {
      return this.$get(this.user, 'claims.access', null)
    },

    ssoCallbackUrl() {
      return this.$get(this.user, 'claims.callback', null)
    },

    /**
     * Returns true if menu toggle should be visible based on available
     * items & current state
     */
    showToggle() {
      const menuItems = filter(this.allMenuItems, (item) => {
          if (item.permissions) {
              return this.$access(item.permissions)
          }

          return true
      })

      return !!menuItems.length && !this.mainMenuOpen
    }
  },

  watch: {
    open(open) {
      this.mainMenuOpen = open
    },
  },

  methods: {
    resolveRoute(route) {
      if (! this.ssoCallbackUrl) {
        return route
      }

      route = route.startsWith('/') ? route.substr(1) : route

      return `${this.ssoCallbackUrl}${route}`
    },

    toggleMainMenu(state) {
      this.mainMenuOpen = state
      this.$emit('toggle', state)
    },

    /**
     * Toggle user menu
     */
    userMenu(event) {
        this.userMenuOpen = event === `toggle` ? !this.userMenuOpen : false
    }
  }
}
</script>

<style lang="scss" scoped>
* {
  --yank-fire: #212A4A;
  --aquamarine: #5BD9A4;
  --yank-mild: #67749E;
  --yank: #181F36;
  --color: var(--yank);
  --platinum-hot: #f7f7f7;
  --platinum: #e8e8e8;
  --payne: #4f5e72;
  --payne-fire: #6c7e99;
  --radius: '4px';
  --element-shadow-focused: '1px 12px 37px rgba(64,64,64,0.1),0px 4px 7px rgba(55,54,77,0.19)';
  --medium: 500;
  --semi-bold: 600;
  --font-size-xs: .75rem;
  --font-size-sm: .875rem;
}

.products {
    flex-direction: column;
    align-items: center;
    width: 70px;
    flex: 0 0 70px;
    padding: 0;
    background: var(--yank-fire);
    z-index: 10;
}

header {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 0 0 78px;
    width: 100%;
    padding: 16px 0 8px;
    color: var(--aquamarine);
    z-index: 2;

    &::before {
        position: absolute;
        content: '';
        width: 100%;
        bottom: -8px;
        height: 8px;
        background-image: linear-gradient(var(--yank-fire), hsla(226, 38%, 21%, 0));
    }

    > * {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    a {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    svg {
        width: 34px;
    }
}

.header-fade {
    &-enter-active,
    &-leave-active {
        transition: all 240ms;
    }

    &-enter,
    &-leave-to {
        opacity: 0;
    }
}

.toggle-main-menu {
    display: flex;
    align-items: center;
    justify-content: center;

    button {
        padding: 0;
        background: none;
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    svg {
        width: 20px;
    }
}

section {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    width: 100%;
    overflow-y: auto;

    // Hides scrollbars
    // IE and Edge
    -ms-overflow-style: none;
    // Firefox
    scrollbar-width: none;

    // Chrome, Safari and Opera
    &::-webkit-scrollbar {
        display: none;
    }

}

.product {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 auto;
    text-align: center;
    margin: 8px 0;

    a {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        margin-bottom: 5px;
        border-radius: 50%;
        color: var(--yank-mild);
        background-color: var(--yank);
        transition: color 240ms, background-color 240ms;

        svg {
            width: 100%;
            height: auto;
            max-width: 24px;
            max-height: 24px;
        }

        &:hover {
            color: white;
        }

        &.link-active {
            color: white;
            background-color: var(--aquamarine);
        }
    }

    .label {
        font-weight: normal;
        font-size: .6875rem;
        line-height: 1.25rem;
        width: 100%;
        padding: 0 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &:last-child {
        padding-bottom: 8px;
    }
}

footer {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 auto;
    width: 100%;
    margin-top: auto;
    padding: 12px 0;
    z-index: 2;

    &::before {
        position: absolute;
        content: '';
        width: 100%;
        top: -8px;
        height: 8px;
        background-image: linear-gradient(hsla(226, 38%, 21%, 0), var(--yank-fire));
    }

    > * + * {
        margin-top: 1.5rem;
    }
}

.help {
    padding: 0;
    background: none;
    outline: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border: 2px solid white;
    border-radius: 50%;

    svg {
        height: 1.125rem;
        width: auto;
    }
}

.popup-menu {
    position: relative;

    button {
        padding: 0;
        background: none;
        border: 0 none;
        outline: none;
    }

    > ul {
        position: absolute;
        bottom: -8px;
        left: calc(100% + 14px);
        width: 230px;
        padding: 0;
        list-style: none;
        background-color: var(--platinum-hot);
        border-radius: var(--radius);
        box-shadow: var(--element-shadow-focused);

        &::after {
            content: '';
            display: block;
            width: 0;
            height: 0;
            border: 6px solid transparent;
            position: absolute;
            z-index: 1;
            border-left-width: 0;
            border-right-color: var(--platinum-hot);
            bottom: 16px;
            left: -6px;
            margin-top: 0;
            margin-bottom: 0;
        }

        .user-details {
            font-size: .875rem;
            line-height: 1.5;
            color: var(--yank-fire);

            .name {
                padding: .25rem .5rem;
            }

            .organization {
                font-weight: var(--medium);
                opacity: 0.5;
            }

            .switch {
                p {
                    font-size: var(--font-size-xs);
                    font-weight: var(--medium);
                    margin: .25rem .5rem 0;
                }

                ul {
                    list-style: none;
                    margin: 0;
                    padding: 0 .25rem .5rem;
                }

                a {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    padding: .5rem;
                    color: var(--color);
                    font-size: var(--font-size-sm);
                    font-weight: var(--semi-bold);
                    background-color: transparent;

                    &:hover {
                        background-color: var(--platinum);
                    }
                }

                .other-name {
                    margin-right: 0.5rem;
                }

                svg {
                    margin-left: auto;
                    --ticon-width: .75rem;
                }
            }
        }

        .separator {
            border-top: 1px solid hsla(226, 38%, 15%, 0.1);
        }

        a {
            display: block;
            padding: 12px 16px;
            white-space: nowrap;
            color: var(--payne-fire);
            background-color: var(--platinum-hot);
            text-decoration: none;
            transition: color 240ms, background 240ms;

            &:hover {
                color: var(--payne);
                background-color: var(--platinum);
            }

            &:focus {
                outline: none;
            }
        }
    }
}
</style>
